import { useAuth } from "components/AuthContext";
import { useDI } from "components/DIContext";
import { jwtDecode } from "jwt-decode";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export enum Role {
    SalesPerson = "SalesPerson",
    CustomerAdmin = "CustomerAdmin",
    Customer = "Customer"
}

export type JWTTokenHook = () => [accessToken: AccessToken | null];

interface RoleProperties {
    roleId: Role
    roleName: string
    companyId: string
    companyName: string
    accountNumber?: string
    erpDataId: string
    siteId: string
}

export interface AccessToken {
    accessToken: string
    authClientId: string
    authTenantId: string
    companyId: string
    type: string
    activeRole: RoleProperties
    roles: RoleProperties[]
    expires: Date
    refreshToken: string
}

const roleMap: Map<string, Role> = new Map<string, Role>(
    [
        ["STW:Nederman:User", Role.SalesPerson],
        ["STW:Nederman:CustAdmin", Role.CustomerAdmin],
        ["STW:Customer:User", Role.Customer],
    ],
)

const isNonEmptyString = (value: unknown): value is string =>
    typeof value === "string" && value.trim().length > 0;

export const constructAccessToken: (accessToken: string, refreshToken: string) => AccessToken = (accessToken: string, refreshToken: string) => {
    let decodedAccessToken = jwtDecode<any>(accessToken);
    let roles = (decodedAccessToken["roles"] as Array<{ roleId: string, roleName: string, companyId: string, companyName: string, accountNumber?: string, erpDataId: string, siteId: string }>).map((val) => {
        if (roleMap.has(val.roleId)) {
            return {
                roleId: roleMap.get(val.roleId),
                roleName: val.roleName,
                companyId: val.companyId,
                companyName: val.companyName,
                accountNumber: val.accountNumber,
                erpDataId: val.erpDataId,
                siteId: val.siteId
            };
        }

        return null
    }).filter((val) => val !== null) as Array<{ roleId: Role, roleName: string, companyId: string, companyName: string, accountNumber?: string, erpDataId: string, siteId: string }>;

    return{
        accessToken: accessToken,
        authClientId: decodedAccessToken["client"] as string,
        authTenantId: decodedAccessToken["tenant"] as string,
        companyId: decodedAccessToken["companyId"],
        type: decodedAccessToken["type"] as string,
        activeRole: roles[0],
        roles: roles,
        expires: (() => {
            let date = new Date(0);
            date.setUTCSeconds(decodedAccessToken["exp"] as number)
            return date;
        })(),
        refreshToken: refreshToken
    };
}

export const useHttpJWTToken: JWTTokenHook = () => {
    const { config } = useDI();
    const [searchParams] = useSearchParams();
    const { token, setToken } = useAuth();

    useEffect(() => {
        const accessToken = searchParams.get("access_token");
        const refreshToken = searchParams.get("refresh_token");

        if (token === null && accessToken === null) {
            window.location.href = config.loginUrl;
            return;
        }

        if (isTokenValid(token)){
            return;
        }

        if (token !== null) {
            return;
        }

        if (accessToken === null) {
            throw new Error("expected access token");
        }

        if (refreshToken === null) {
            throw new Error("expected refresh token");
        }

        setToken(constructAccessToken(accessToken, refreshToken));
    }, [config.loginUrl, searchParams, setToken, token]);

    return [token];
};

export const useMockedToken: JWTTokenHook = () => {
    const { token, setToken } = useAuth();

    setToken({
        accessToken: "mock",
        authClientId: "mock",
        authTenantId: "mock",
        companyId: "DK2",
        type: "NEDERMAN",
        activeRole: {
            roleId: Role.SalesPerson,
            roleName: "sales",
            companyId: "mock",
            companyName: "mock",
            accountNumber: undefined,
            erpDataId: "DK2",
            siteId: "mock"
        },
        roles: [
            {
                roleId: Role.SalesPerson,
                roleName: "sales",
                companyId: "mock",
                companyName: "mock",
                accountNumber: undefined,
                erpDataId: "DK2",
                siteId: "mock"
            }
        ],
        expires: new Date(),
        refreshToken: "mock"
    });



    return [token];
};

const isValidRole = (value: unknown): value is RoleProperties => {
    if (typeof value !== "object" || value === null) {
        return false;
    }

    const role = value as RoleProperties;

    return (
        roleMapHasValue(role.roleId) &&
        isNonEmptyString(role.roleName) &&
        isNonEmptyString(role.companyId) &&
        isNonEmptyString(role.companyName) &&
        (role.accountNumber === undefined || isNonEmptyString(role.accountNumber)) &&
        isNonEmptyString(role.erpDataId) &&
        isNonEmptyString(role.siteId)
    );
};

const roleMapHasValue = (role: Role): boolean =>
    Array.from(roleMap.values()).includes(role);

export const isTokenValid = (
    token: AccessToken | null,
    validateRoles: boolean = true,
    bufferMilliseconds = 30_000
): token is AccessToken => {
    if (!token) {
        return false;
    }

    const expiresAt = new Date(token.expires).getTime();

    return (
        isNonEmptyString(token.accessToken) &&
        isNonEmptyString(token.authClientId) &&
        isNonEmptyString(token.authTenantId) &&
        isNonEmptyString(token.companyId) &&
        isNonEmptyString(token.type) &&
        isNonEmptyString(token.refreshToken) &&
        Number.isFinite(expiresAt) &&
        expiresAt > Date.now() + bufferMilliseconds &&
        (!validateRoles || (
            Array.isArray(token.roles) &&
            token.roles.length > 0 &&
            token.roles.every(isValidRole) &&
            isValidRole(token.activeRole)
        ))
    );
};